// src/hooks/use-did-you-know.ts
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DidYouKnowTip,
  getNextSessionTip,
  getRandomUnshownTip,
  getRandomTipForPage,
  getTipsForPage
} from '@/data/did-you-know-tips';

const SESSION_STORAGE_KEY_SHOWN_TIPS = 'didYouKnow_sessionShownTips'; // Session-only tracking
const STORAGE_KEY_SESSION_SHOWN = 'didYouKnow_sessionShown';
const STORAGE_KEY_ENABLED = 'didYouKnow_enabled';
const SESSION_STORAGE_KEY = 'didYouKnow_sessionId';

/**
 * Custom hook for managing "Did You Know" tips
 * Shows contextual tips based on current page, with manual controls
 */
export function useDidYouKnow() {
  const location = useLocation();
  const [currentTip, setCurrentTip] = useState<DidYouKnowTip | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);
  const [shownTipIds, setShownTipIds] = useState<string[]>([]);

  // Check if current page has specific tips available
  const hasPageTips = getTipsForPage(location.pathname, shownTipIds).length > 0;

  // Load shown tips from sessionStorage (session-only tracking)
  useEffect(() => {
    const sessionShownJson = sessionStorage.getItem(SESSION_STORAGE_KEY_SHOWN_TIPS);
    const sessionShownIds: string[] = sessionShownJson ? JSON.parse(sessionShownJson) : [];
    setShownTipIds(sessionShownIds);
  }, []);

  // Initialize session tracking and show first tip
  useEffect(() => {
    // Check if tips are enabled
    const enabled = localStorage.getItem(STORAGE_KEY_ENABLED);
    if (enabled === 'false') {
      setIsEnabled(false);
      return;
    }

    // Check if this is a new session
    const currentSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const now = Date.now().toString();

    if (!currentSessionId) {
      // New session - clear session flag
      sessionStorage.setItem(SESSION_STORAGE_KEY, now);
      localStorage.removeItem(STORAGE_KEY_SESSION_SHOWN);
    }

    // Check if we've already shown a tip this session
    const sessionShown = localStorage.getItem(STORAGE_KEY_SESSION_SHOWN) === 'true';

    // Get session-shown tip IDs
    const sessionShownJson = sessionStorage.getItem(SESSION_STORAGE_KEY_SHOWN_TIPS);
    const sessionShownIds: string[] = sessionShownJson ? JSON.parse(sessionShownJson) : [];

    // Get the next tip to show (prioritizes page-specific tips)
    const tip = getNextSessionTip(sessionShown, sessionShownIds, location.pathname);

    if (tip) {
      setCurrentTip(tip);
      setIsVisible(true);

      // Mark session as shown
      localStorage.setItem(STORAGE_KEY_SESSION_SHOWN, 'true');

      // Add to session-shown list
      const updatedShownIds = [...sessionShownIds, tip.id];
      sessionStorage.setItem(SESSION_STORAGE_KEY_SHOWN_TIPS, JSON.stringify(updatedShownIds));
      setShownTipIds(updatedShownIds);
    }
  }, [location.pathname]);

  // Dismiss the current tip
  const dismissTip = useCallback(() => {
    setIsVisible(false);
    // Add a small delay before removing from DOM for animation
    setTimeout(() => {
      setCurrentTip(null);
    }, 300);
  }, []);

  // Show another random tip (prioritize page-specific tips first)
  const showAnotherTip = useCallback(() => {
    // Try to get a page-specific tip first
    let tip = getRandomTipForPage(location.pathname, shownTipIds);

    // If no page-specific tips left, get any unshown tip
    if (!tip) {
      tip = getRandomUnshownTip(shownTipIds);
    }

    if (tip) {
      setCurrentTip(tip);
      setIsVisible(true);

      // Add to session-shown list
      const updatedShownIds = [...shownTipIds, tip.id];
      sessionStorage.setItem(SESSION_STORAGE_KEY_SHOWN_TIPS, JSON.stringify(updatedShownIds));
      setShownTipIds(updatedShownIds);
    } else {
      // All tips shown - could reset or show message
      dismissTip();
    }
  }, [location.pathname, shownTipIds, dismissTip]);

  // Show a tip specific to current page
  const showPageTip = useCallback(() => {
    const tip = getRandomTipForPage(location.pathname, shownTipIds);

    if (tip) {
      setCurrentTip(tip);
      setIsVisible(true);

      // Add to session-shown list
      const updatedShownIds = [...shownTipIds, tip.id];
      sessionStorage.setItem(SESSION_STORAGE_KEY_SHOWN_TIPS, JSON.stringify(updatedShownIds));
      setShownTipIds(updatedShownIds);
    } else {
      // No page-specific tips available - fall back to any tip
      showAnotherTip();
    }
  }, [location.pathname, shownTipIds, showAnotherTip]);

  // Toggle tips on/off
  const toggleEnabled = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
    localStorage.setItem(STORAGE_KEY_ENABLED, enabled.toString());

    if (!enabled) {
      setIsVisible(false);
      setCurrentTip(null);
    }
  }, []);

  // Reset all shown tips (useful for testing or user preference)
  const resetShownTips = useCallback(() => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY_SHOWN_TIPS);
    localStorage.removeItem(STORAGE_KEY_SESSION_SHOWN);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setShownTipIds([]);
  }, []);

  return {
    currentTip,
    isVisible,
    isEnabled,
    hasPageTips,
    dismissTip,
    showAnotherTip,
    showPageTip,
    toggleEnabled,
    resetShownTips,
  };
}
